import type { NFTI } from '@tacans/shared-marketplace-layer';
import React, { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { PreloadImage } from 'src/features/common/app/components/preload-image';
import { useIsWebpSupported } from 'src/hooks/use-is-webp-supported';
import { StyledLazyLoadImage, StyledVideoWrapper } from './nft-card-media.styles';

interface Props {
    nft: NFTI;
    className?: string;
    preload?: boolean;
}

export const NftCardMedia: React.VFC<Props> = ({ preload, nft, className }) => {
    const isPreviewGif = nft?.previewAssets?.originalUrl.slice(-3) === 'gif';
    const videoUrl = nft?.previewAssets?.videos?.mediumUrl ?? nft?.previewAssets?.videos?.smallUrl;

    const { previewAssets } = nft || {};
    const isWebpSupported = useIsWebpSupported();
    const imageSrc =
        isWebpSupported && previewAssets?.webps?.mediumUrl
            ? previewAssets?.webps?.mediumUrl
            : previewAssets?.originalUrl;

    const videoRef = useRef<HTMLVideoElement | undefined>();

    const { ref, inView } = useInView({
        triggerOnce: true,
    });

    const handleRightClick = useCallback((event: React.MouseEvent<HTMLImageElement>) => event.preventDefault(), []);

    useEffect(() => {
        if (inView && videoRef.current) {
            videoRef.current.play();
        }
        if (!inView && videoRef.current) {
            videoRef.current.pause();
        }
    }, [inView]);

    return (
        <div ref={ref} className={className}>
            {!!preload && imageSrc && <PreloadImage src={imageSrc} />}
            {!!videoUrl && !isPreviewGif && (
                <StyledVideoWrapper>
                    <div>
                        {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
                        <video
                            muted
                            src={nft?.previewAssets?.originalUrl}
                            ref={(ref) => {
                                // @ts-ignore
                                videoRef.current = ref;
                            }}
                            playsInline
                            loop
                            autoPlay
                            controls={false}
                            controlsList="nodownload"
                        >
                            <source src={videoUrl} type={nft.contentType} />
                            <track kind="captions" srcLang="en" label="english_captions" />
                        </video>
                    </div>
                </StyledVideoWrapper>
            )}
            {!videoUrl && imageSrc && <StyledLazyLoadImage src={imageSrc} onContextMenu={handleRightClick} />}
            {isPreviewGif && (
                <StyledLazyLoadImage src={nft?.previewAssets?.originalUrl} onContextMenu={handleRightClick} />
            )}
        </div>
    );
};